import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Collapse,
  Drawer,
  InputNumber,
  Modal,
  Radio,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
  notification,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from 'util/api.js';
import { priceFieldGreyOut } from 'util/pricing-fields';
import {
  invalidatePricingDiscrepancyQueries,
  PRICING_DISCREPANCY_BEER_KEY,
  PRICING_DISCREPANCY_SCAN_KEY,
  usePricingDiscrepancyBeerQuery,
} from './use-pricing-discrepancy-scan-query';
import SkuFieldTable from './SkuFieldTable';
import PushPreview, { buildPushPreview, kountaCostPreview } from './PushPreview';
import SkuLinksPanel from './SkuLinksPanel';
import {
  beerDisplayName,
  cin7ProductHref,
  collectFieldSlots,
  collectUniqueFieldKeys,
  errorMessage,
  findSlotForField,
  formatMoney,
  getStore,
  kountaProductHref,
  matrixProductHref,
  mondaySkuPulseHref,
  SHOPIFY_COMPARED_STORE,
  skuIssueCounts,
  skuSystemCounts,
  stopRowClick,
  SYSTEM_LABELS,
  SYSTEMS,
} from './helpers';

const { Paragraph, Text } = Typography;
const { Panel } = Collapse;
const EMPTY_DTC_PRICES = [];

const toCamel = (s) => s.replace(/_([a-z0-9])/gi, (_, c) => c.toUpperCase());

/** Apply `priceFieldGreyOut` so fields that do not apply to the SKU format are hidden. */
function visibleComparisonFields(sku) {
  const rows = sku?.comparisonFields || [];
  if (!sku) return rows;
  const meta = {
    containerType: sku.containerType,
    unitType: sku.unitType,
    caseType: sku.caseType,
    productRange: sku.productRange,
    productType: sku.productType,
  };
  return rows.filter((r) => {
    if (r.rowKind === 'cost_driver' || r.rowKind === 'matrix_only') {
      return !priceFieldGreyOut(meta, toCamel(r.field));
    }
    if (r.rowKind === 'kounta_line') {
      if (!r.sell?.field) return false;
      return !priceFieldGreyOut(meta, toCamel(r.sell.field));
    }
    return true;
  });
}

const ISSUE_ROW_TYPES = ['discrepancy', 'missing', 'misalignment'];

/** Red, orange, and green comparison rows. Older payloads fall back to the discrepancy flag. */
function rowIsIssue(row) {
  if (!row) return false;
  if (row.issueType) return ISSUE_ROW_TYPES.includes(row.issueType);
  return Boolean(row.hasDiscrepancy);
}

/** Guide-derived resolutions for every visible field that is both Guide-covered and mismatched. */
function guideResolutionsForSku(sku) {
  const seen = new Set();
  const out = [];
  visibleComparisonFields(sku).forEach((row) => {
    const actionable = rowIsIssue(row);
    if (!actionable) return;
    collectFieldSlots(row).forEach((s) => {
      if (seen.has(s.field)) return;
      if (!s.guide?.available || s.guide.value == null) return;
      seen.add(s.field);
      out.push({ field: s.field, value: String(s.guide.value) });
    });
  });
  return out;
}

function invalidOverrideFields(fields, selections, overrides) {
  return fields.filter((field) => {
    if (selections[field] !== 'override') return false;
    const raw = overrides[field];
    if (raw === null || raw === undefined || raw === '') return true;
    return Number.isNaN(Number(raw));
  });
}

function fieldListModal(title, fields, rows) {
  Modal.error({
    title,
    content: (
      <ul>
        {fields.map((field) => (
          <li key={field}>{findSlotForField(rows, field)?.label || field}</li>
        ))}
      </ul>
    ),
  });
}

function ExternalLink({ href, title, children }) {
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" title={title} onClick={stopRowClick}>
      {children}
    </a>
  );
}

ExternalLink.propTypes = {
  href: PropTypes.string,
  title: PropTypes.string,
  children: PropTypes.node,
};

ExternalLink.defaultProps = {
  href: null,
  title: undefined,
  children: null,
};

function SkuPanelHeader({
  sku, onToggleLegacy, legacyBusy, onOpenLedger,
}) {
  const counts = skuSystemCounts(sku);
  const issues = skuIssueCounts(sku);
  const clean = issues.discrepancy === 0 && issues.missing === 0 && issues.misalignment === 0;
  const kountaProducts = sku.kounta?.products || [];
  const kountaId = kountaProducts[0]?.kountaProductId;
  return (
    <Space wrap size="small" align="center">
      <Text strong>{sku.skuName}</Text>
      <Text type="secondary" style={{ fontSize: 12 }}>
        #
        {sku.skuKey}
      </Text>
      {sku.isLegacy ? <Tag>Legacy</Tag> : null}
      {SYSTEMS.map((sys) => (counts[sys] > 0 ? (
        <Tag key={sys} color="error" style={{ marginRight: 0 }}>
          {SYSTEM_LABELS[sys]}
          {' '}
          {counts[sys]}
        </Tag>
      ) : null))}
      {issues.missing > 0 ? (
        <Tag color="warning" style={{ marginRight: 0 }}>
          Missing
          {' '}
          {issues.missing}
        </Tag>
      ) : null}
      {issues.misalignment > 0 ? (
        <Tag color="success" style={{ marginRight: 0 }}>
          Misalignment
          {' '}
          {issues.misalignment}
        </Tag>
      ) : null}
      {clean ? <Tag color="success">OK</Tag> : null}
      <span onClick={stopRowClick} role="presentation">
        <Switch
          size="small"
          checked={Boolean(sku.isLegacy)}
          loading={legacyBusy}
          onChange={(checked) => onToggleLegacy(sku, checked)}
        />
        <Text type="secondary" style={{ marginLeft: 6 }}>Legacy</Text>
        <Button
          type="link"
          size="small"
          onClick={() => onOpenLedger(sku.skuKey)}
        >
          Ledger
        </Button>
      </span>
      <Space size="small" style={{ fontSize: 12 }}>
        <ExternalLink href={matrixProductHref(sku.productMatrixId)} title="Open product in Matrix">
          Matrix
        </ExternalLink>
        <ExternalLink href={mondaySkuPulseHref(sku.mondayId)} title="Open in Monday">
          Monday
        </ExternalLink>
        <ExternalLink href={cin7ProductHref(sku.dearCode)} title="Open in Cin7 (DEAR)">
          {sku.cin7Sku ? `Cin7 ${sku.cin7Sku}` : 'Cin7'}
        </ExternalLink>
        <ExternalLink href={kountaProductHref(kountaId)} title="Open in Kounta">
          {kountaProducts.length > 1 ? `Kounta +${kountaProducts.length - 1}` : 'Kounta'}
        </ExternalLink>
      </Space>
    </Space>
  );
}

SkuPanelHeader.propTypes = {
  sku: PropTypes.shape({
    skuKey: PropTypes.number,
    skuName: PropTypes.string,
    isLegacy: PropTypes.bool,
    productMatrixId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    mondayId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dearCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    cin7Sku: PropTypes.string,
    totalDiscrepancies: PropTypes.number,
    issueCounts: PropTypes.shape({
      missing: PropTypes.number,
      misalignment: PropTypes.number,
    }),
    kounta: PropTypes.shape({
      products: PropTypes.arrayOf(PropTypes.shape({
        kountaProductId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })),
    }),
  }).isRequired,
  onToggleLegacy: PropTypes.func.isRequired,
  legacyBusy: PropTypes.bool,
  onOpenLedger: PropTypes.func.isRequired,
};

SkuPanelHeader.defaultProps = {
  legacyBusy: false,
};

function Cin7TierTable({ cin7, compact }) {
  if (!cin7?.linked) {
    return <Text type="secondary">{cin7?.reason || 'Not linked to Cin7'}</Text>;
  }
  const rows = (cin7.tierRows || []).filter((r) => (
    compact ? r.managed !== false && r.status === 'mismatch' : true
  ));
  if (compact && rows.length === 0) {
    return <Text type="secondary">No tier mismatches.</Text>;
  }
  return (
    <Table
      size="small"
      pagination={false}
      dataSource={rows}
      rowKey={(r) => r.tierNum ?? r.purpose}
      columns={[
        {
          title: 'Tier',
          dataIndex: 'tierNum',
          width: 70,
          render: (v, r) => (r.managed === false ? `${v} (n/a)` : v),
        },
        { title: 'Purpose', dataIndex: 'purpose' },
        {
          title: 'Expected',
          key: 'exp',
          render: (_, r) => (r.managed === false ? '—' : formatMoney(r.expected)),
        },
        {
          title: 'Actual (cache)',
          key: 'act',
          render: (_, r) => formatMoney(r.actual),
        },
        {
          title: '',
          key: 'st',
          width: 110,
          render: (_, r) => {
            if (r.managed === false) return <Tag color="default">Not managed</Tag>;
            if (r.status === 'mismatch') return <Tag color="error">Mismatch</Tag>;
            return <Tag color="success">OK</Tag>;
          },
        },
      ]}
    />
  );
}

Cin7TierTable.propTypes = {
  cin7: PropTypes.shape({
    linked: PropTypes.bool,
    reason: PropTypes.string,
    tierRows: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  compact: PropTypes.bool,
};

Cin7TierTable.defaultProps = {
  cin7: null,
  compact: false,
};

function KountaLinesTable({
  rows, compact, disabled, busy, onApplyCosts,
}) {
  const lines = rows.filter((r) => r.rowKind === 'kounta_line').filter((r) => {
    if (!compact) return true;
    const costBad = Boolean(r.kountaCostMismatch) || r.kountaLine?.cost?.match === false;
    return r.issueType === 'discrepancy' || costBad;
  });
  const [selections, setSelections] = useState({});
  const [overrides, setOverrides] = useState({});

  const clearCost = useCallback((key) => {
    setSelections((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const selectExpected = useCallback((key) => {
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setSelections((prev) => ({ ...prev, [key]: 'expected' }));
  }, []);

  const applyCosts = useCallback(() => {
    const bad = [];
    const items = [];
    lines.forEach((row) => {
      const key = String(row.kountaRowId);
      const src = selections[key];
      if (!src) return;
      if (src === 'override') {
        const raw = overrides[key];
        if (raw === null || raw === undefined || raw === '' || Number.isNaN(Number(raw))) {
          bad.push(row.productName || key);
          return;
        }
        items.push({ kountaRowId: row.kountaRowId, value: raw });
        return;
      }
      const expected = row.kountaLine?.cost?.expected;
      if (expected == null) {
        bad.push(row.productName || key);
        return;
      }
      items.push({ kountaRowId: row.kountaRowId, value: expected });
    });
    if (bad.length > 0) {
      Modal.error({
        title: 'Enter a valid cost',
        content: (
          <ul>
            {bad.map((name) => <li key={name}>{name}</li>)}
          </ul>
        ),
      });
      return;
    }
    if (items.length === 0) return;
    onApplyCosts(items);
  }, [lines, selections, overrides, onApplyCosts]);

  if (lines.length === 0) {
    return (
      <Text type="secondary">
        {compact ? 'No Kounta mismatches.' : 'No linked Kounta products.'}
      </Text>
    );
  }
  const cmpCell = (c) => {
    if (!c) return <Text type="secondary">—</Text>;
    if (c.applicable === false) return <Text type="secondary">n/a</Text>;
    return (
      <Text type={c.match === false ? 'danger' : undefined}>
        {formatMoney(c.actual)}
        {c.match === false ? ` (exp ${formatMoney(c.expected)})` : ''}
      </Text>
    );
  };
  const selectedCount = Object.keys(selections).length;
  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <Table
        size="small"
        pagination={false}
        dataSource={lines}
        rowKey={(r) => r.rowKey}
        onRow={(r) => {
          const costBad = Boolean(r.kountaCostMismatch) || r.kountaLine?.cost?.match === false;
          if (costBad) return { style: { background: '#fff1f0' } };
          return {};
        }}
        columns={[
          {
            title: 'Kounta product',
            key: 'name',
            ellipsis: true,
            render: (_, r) => (
              <ExternalLink href={kountaProductHref(r.kountaProductId)} title="Open in Kounta">
                {r.productName || r.kountaProductId}
              </ExternalLink>
            ),
          },
          { title: 'Serving', dataIndex: 'servingType', width: 150 },
          {
            title: 'Sell (inc GST)',
            key: 'sell',
            width: 150,
            render: (_, r) => cmpCell(r.kountaLine?.sell),
          },
          {
            title: 'Cost (inc GST)',
            key: 'cost',
            width: 170,
            render: (_, r) => cmpCell(r.kountaLine?.cost),
          },
          {
            title: 'Expected cost',
            key: 'expected',
            width: 140,
            render: (_, r) => {
              const cost = r.kountaLine?.cost;
              if (!cost || cost.applicable === false || cost.expected == null) {
                return <Text type="secondary">—</Text>;
              }
              const key = String(r.kountaRowId);
              const checked = selections[key] === 'expected';
              return (
                <Radio
                  checked={checked}
                  disabled={disabled}
                  onClick={(e) => {
                    if (!checked) return;
                    e.preventDefault();
                    clearCost(key);
                  }}
                  onChange={() => {
                    if (checked) return;
                    selectExpected(key);
                  }}
                >
                  {formatMoney(cost.expected)}
                </Radio>
              );
            },
          },
          {
            title: 'Overwrite',
            key: 'overwrite',
            width: 120,
            render: (_, r) => {
              const cost = r.kountaLine?.cost;
              if (!cost || cost.applicable === false) return <Text type="secondary">—</Text>;
              const key = String(r.kountaRowId);
              return (
                <InputNumber
                  size="small"
                  style={{ width: 105 }}
                  min={0}
                  step={0.01}
                  placeholder="New price"
                  disabled={disabled}
                  value={overrides[key] ?? null}
                  onChange={(val) => {
                    if (val === null || val === undefined) {
                      clearCost(key);
                      return;
                    }
                    setOverrides((prev) => ({ ...prev, [key]: val }));
                    setSelections((prev) => ({ ...prev, [key]: 'override' }));
                  }}
                />
              );
            },
          },
          {
            title: '',
            key: 'clear',
            width: 64,
            render: (_, r) => {
              const key = String(r.kountaRowId);
              if (selections[key] == null) return null;
              return (
                <Button
                  type="link"
                  size="small"
                  disabled={disabled}
                  style={{ padding: 0 }}
                  onClick={() => clearCost(key)}
                >
                  Clear
                </Button>
              );
            },
          },
        ]}
      />
      <Space>
        <Button
          type="primary"
          size="small"
          loading={busy}
          disabled={disabled || selectedCount === 0}
          onClick={applyCosts}
        >
          Apply cost
          {selectedCount > 0 ? ` (${selectedCount})` : ''}
        </Button>
      </Space>
    </Space>
  );
}

KountaLinesTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  compact: PropTypes.bool,
  disabled: PropTypes.bool,
  busy: PropTypes.bool,
  onApplyCosts: PropTypes.func.isRequired,
};

KountaLinesTable.defaultProps = {
  compact: false,
  disabled: false,
  busy: false,
};

function SkuPanelBody({
  sku, disabled, busy, onResolve, compact, beerName, sharedCin7, sharedKounta, onLinksChanged,
  onKountaCatalogSkuChange, highlightMissingLinks,
}) {
  const allRows = useMemo(() => visibleComparisonFields(sku), [sku]);
  const rows = useMemo(() => {
    if (!compact) return allRows;
    return allRows.filter(rowIsIssue);
  }, [allRows, compact]);
  const hiddenCount = allRows.length - rows.length;

  const [selections, setSelections] = useState({});
  const [overrides, setOverrides] = useState({});

  const selectAll = useCallback((source) => {
    const next = {};
    const cleared = new Set();
    rows.forEach((row) => {
      collectFieldSlots(row).forEach((s) => {
        let ok = false;
        if (source === 'matrix') ok = true;
        else if (source === 'guide') ok = Boolean(s.guide?.available && s.guide.value != null);
        else if (source === 'monday') ok = Boolean(s.monday?.available);
        else if (source === 'cin7') ok = Boolean(s.cin7?.available && s.cin7?.reversible);
        if (ok) {
          next[s.field] = source;
          cleared.add(s.field);
        }
      });
    });
    setOverrides((prev) => {
      const o = { ...prev };
      cleared.forEach((f) => delete o[f]);
      return o;
    });
    setSelections((prev) => ({ ...prev, ...next }));
  }, [rows]);

  const clearAll = useCallback(() => {
    setSelections({});
    setOverrides({});
  }, []);

  const applySelected = useCallback(() => {
    const fieldKeys = collectUniqueFieldKeys(rows).filter((f) => f in selections);
    if (fieldKeys.length === 0) {
      Modal.error({
        title: 'No fields selected',
        content: 'Select a source or enter an overwrite for at least one field.',
      });
      return;
    }
    const badOverrides = invalidOverrideFields(fieldKeys, selections, overrides);
    if (badOverrides.length > 0) {
      fieldListModal('Enter a valid override price', badOverrides, rows);
      return;
    }
    const resolutions = fieldKeys.map((field) => {
      const slot = findSlotForField(rows, field);
      const src = selections[field];
      let value;
      if (src === 'guide') value = slot?.guide?.value;
      else if (src === 'monday') value = slot?.monday?.value;
      else if (src === 'matrix') value = slot?.matrix?.value;
      else if (src === 'cin7') value = slot?.cin7?.value;
      else if (src === 'shopify') {
        value = getStore(slot?.shopify, SHOPIFY_COMPARED_STORE)?.value;
      } else if (src === 'kounta') {
        const row = rows.find((r) => collectFieldSlots(r).some((sl) => sl.field === field));
        value = row?.kountaLine?.sell?.actual;
      } else if (src === 'override') value = overrides[field];
      return { field, value: value != null && value !== '' ? String(value) : '' };
    });
    onResolve({ skuKey: sku.skuKey, resolutions });
  }, [rows, selections, overrides, onResolve, sku.skuKey]);

  const selectedCount = Object.keys(selections).length;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <SkuLinksPanel
        sku={sku}
        beerName={beerName}
        disabled={disabled || busy}
        sharedCin7={sharedCin7}
        sharedKounta={sharedKounta}
        onChanged={onLinksChanged}
        onKountaCatalogSkuChange={onKountaCatalogSkuChange}
        highlightMissingLinks={highlightMissingLinks}
      />
      {compact && hiddenCount > 0 ? (
        <Text type="secondary">
          {hiddenCount}
          {' '}
          clean row(s) hidden. Turn off Only issue rows to see every price.
        </Text>
      ) : null}
      {rows.length > 0 ? (
        <SkuFieldTable
          rows={rows}
          selections={selections}
          setSelections={setSelections}
          overrides={overrides}
          setOverrides={setOverrides}
          disabled={disabled || busy}
        />
      ) : (
        <Text type="secondary">No field data for this SKU (run a new scan).</Text>
      )}
      <Space wrap>
        <Button size="small" onClick={() => selectAll('guide')} disabled={disabled || busy}>
          Select all Guide
        </Button>
        <Button size="small" onClick={() => selectAll('matrix')} disabled={disabled || busy}>
          Select all Matrix
        </Button>
        <Button size="small" onClick={() => selectAll('monday')} disabled={disabled || busy}>
          Select all Monday
        </Button>
        <Button size="small" onClick={() => selectAll('cin7')} disabled={disabled || busy}>
          Select all Cin7
        </Button>
        <Button size="small" onClick={clearAll} disabled={disabled || busy || selectedCount === 0}>
          Clear
        </Button>
        <Button
          type="primary"
          size="small"
          loading={busy}
          disabled={disabled || selectedCount === 0}
          onClick={applySelected}
        >
          Apply selected
          {selectedCount > 0 ? ` (${selectedCount})` : ''}
        </Button>
      </Space>
      <Collapse
        ghost
        defaultActiveKey={allRows.some((r) => (
          r.kountaCostMismatch || r.kountaLine?.cost?.match === false
        )) ? ['kounta'] : []}
      >
        <Panel header="Cin7 (NZ trade tiers)" key="cin7">
          <Cin7TierTable cin7={sku.cin7} compact={compact} />
        </Panel>
        <Panel header="Kounta lines" key="kounta">
          <KountaLinesTable
            rows={allRows}
            compact={compact}
            disabled={disabled || busy}
            busy={busy}
            onApplyCosts={(kountaCosts) => onResolve({
              skuKey: sku.skuKey,
              kountaCosts,
            })}
          />
        </Panel>
      </Collapse>
    </Space>
  );
}

SkuPanelBody.propTypes = {
  sku: PropTypes.shape({
    skuKey: PropTypes.number,
    containerType: PropTypes.string,
    comparisonFields: PropTypes.arrayOf(PropTypes.shape({})),
    cin7: PropTypes.shape({}),
    kounta: PropTypes.shape({
      products: PropTypes.arrayOf(PropTypes.shape({
        kountaProductId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })),
    }),
  }).isRequired,
  disabled: PropTypes.bool.isRequired,
  busy: PropTypes.bool.isRequired,
  onResolve: PropTypes.func.isRequired,
  compact: PropTypes.bool,
  beerName: PropTypes.string,
  sharedCin7: PropTypes.arrayOf(PropTypes.shape({})),
  sharedKounta: PropTypes.arrayOf(PropTypes.shape({})),
  onLinksChanged: PropTypes.func,
  onKountaCatalogSkuChange: PropTypes.func,
  highlightMissingLinks: PropTypes.bool,
};

SkuPanelBody.defaultProps = {
  compact: true,
  beerName: '',
  sharedCin7: undefined,
  sharedKounta: undefined,
  onLinksChanged: undefined,
  onKountaCatalogSkuChange: undefined,
  highlightMissingLinks: false,
};

function notifyResolveResult(message, { shopifyErrors, cin7Error } = {}) {
  const parts = [];
  const shopify = (shopifyErrors || []).filter(Boolean);
  if (shopify.length > 0) parts.push(`Shopify: ${shopify.join('; ')}`);
  if (cin7Error) parts.push(`Cin7: ${cin7Error}`);
  if (parts.length > 0) {
    notification.warning({
      message,
      description: parts.join(' '),
    });
    return;
  }
  notification.success({ message });
}

/**
 * Expanded resolver drawer for one beer: Shopify link header, one Collapse panel per SKU, and a
 * footer that applies Guide values to every mismatched Guide-covered field in the beer.
 */
export default function BeerDrawer({
  beerKey, beerSummary, open, onClose, disabled, includeLegacy, highlightMissingLinks,
  onOpenLedger,
}) {
  const queryClient = useQueryClient();
  const linksDirtyRef = useRef(false);
  const [resetVersion, setResetVersion] = useState(0);
  const [compact, setCompact] = useState(true);
  const [legacySkuKey, setLegacySkuKey] = useState(null);
  const [pendingPush, setPendingPush] = useState(null);
  const [pushOkReady, setPushOkReady] = useState(false);
  const detail = usePricingDiscrepancyBeerQuery(beerKey, open, includeLegacy);
  const beer = detail.data || null;
  const titleBeer = beer || beerSummary;

  const afterWrite = useCallback(() => {
    linksDirtyRef.current = false;
    invalidatePricingDiscrepancyQueries(queryClient);
    setResetVersion((v) => v + 1);
  }, [queryClient]);

  const refreshBeerDetail = useCallback(() => {
    linksDirtyRef.current = true;
    queryClient.invalidateQueries({
      queryKey: [
        ...PRICING_DISCREPANCY_BEER_KEY,
        beerKey,
        includeLegacy ? 'legacy' : 'active',
      ],
    });
  }, [queryClient, beerKey, includeLegacy]);

  const patchKountaCatalog = useCallback((kountaId, productSkuKey) => {
    const current = queryClient.getQueryData(['pricing_discrepancy_kounta_catalog']);
    if (!Array.isArray(current)) {
      queryClient.invalidateQueries({ queryKey: ['pricing_discrepancy_kounta_catalog'] });
      return;
    }
    queryClient.setQueryData(['pricing_discrepancy_kounta_catalog'], (prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((item) => (
        String(item.id) === String(kountaId)
          ? {
            ...item,
            productSkuKey,
          }
          : item
      ));
    });
  }, [queryClient]);

  const cin7Catalog = useQuery({
    queryKey: ['pricing_discrepancy_cin7_catalog'],
    queryFn: () => api.getCachedCin7Products(),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });
  const kountaCatalog = useQuery({
    queryKey: ['pricing_discrepancy_kounta_catalog'],
    queryFn: () => api.getKountaProducts(),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });
  let sharedKountaCatalog;
  if (Array.isArray(kountaCatalog.data)) {
    sharedKountaCatalog = kountaCatalog.data;
  } else if (kountaCatalog.isFetching) {
    sharedKountaCatalog = null;
  }
  const dtcPricesQuery = useQuery({
    queryKey: ['dtc_prices'],
    queryFn: () => api.getDtcPricing(),
    enabled: open,
    staleTime: Infinity,
  });
  const dtcPrices = dtcPricesQuery.data || EMPTY_DTC_PRICES;

  const legacyMutation = useMutation({
    mutationFn: ({ sku, isLegacy }) => api.updateProductSkuStrict(
      sku.productMatrixId,
      sku.skuKey,
      { isLegacy },
    ),
    onSuccess: () => {
      notification.success({ message: 'Legacy flag saved' });
      afterWrite();
    },
    onError: (err) => notification.error({ message: errorMessage(err, 'Could not update Legacy') }),
    onSettled: () => setLegacySkuKey(null),
  });

  const resolveMutation = useMutation({
    mutationFn: (payload) => api.resolvePricingDiscrepancies(payload),
    onSuccess: (data) => {
      const buffer = data?.accumulatedKountaCsvRowCount ?? 0;
      notifyResolveResult(
        `Changes applied. Kounta queue: ${buffer} row(s) pending export.`,
        { shopifyErrors: data?.shopifyErrors, cin7Error: data?.cin7Error },
      );
      setPendingPush(null);
      afterWrite();
    },
    onError: (err) => notification.error({ message: errorMessage(err, 'Resolve failed') }),
  });

  const bulkMutation = useMutation({
    mutationFn: (items) => api.resolvePricingDiscrepanciesBulk({ items }),
    onSuccess: (data, items) => {
      const results = data?.results || [];
      const failed = results.filter((r) => r && r.ok === false);
      const shopifyNotes = results.flatMap((r) => (
        (r?.shopifyErrors || []).map((err) => `#${r.skuKey}: ${err}`)
      ));
      const cin7Notes = results
        .filter((r) => r?.cin7Error)
        .map((r) => `#${r.skuKey}: ${r.cin7Error}`);
      const descriptionParts = [];
      if (failed.length > 0) {
        descriptionParts.push(
          failed.map((f) => `#${f.skuKey}: ${f.error || 'failed'}`).join('; '),
        );
      }
      if (shopifyNotes.length > 0) {
        descriptionParts.push(`Shopify: ${shopifyNotes.join('; ')}`);
      }
      if (cin7Notes.length > 0) {
        descriptionParts.push(`Cin7: ${cin7Notes.join('; ')}`);
      }
      if (descriptionParts.length > 0) {
        const applied = items.length - failed.length;
        notification.warning({
          message: `Guide applied to ${applied}/${items.length} SKU(s)`,
          description: descriptionParts.join(' '),
        });
      } else {
        notification.success({ message: `Guide prices applied to ${items.length} SKU(s).` });
      }
      setPendingPush(null);
      afterWrite();
      onClose();
    },
    onError: (err) => notification.error({ message: errorMessage(err, 'Bulk apply failed') }),
  });

  const busy = resolveMutation.isLoading || bulkMutation.isLoading;

  const skus = beer?.skus || [];

  const bulkItems = useMemo(() => skus
    .map((sku) => ({ skuKey: sku.skuKey, resolutions: guideResolutionsForSku(sku) }))
    .filter((it) => it.resolutions.length > 0), [skus]);
  const bulkFieldCount = bulkItems.reduce((n, it) => n + it.resolutions.length, 0);

  const defaultActiveKeys = useMemo(() => {
    const keys = skus
      .filter((s) => {
        const issues = skuIssueCounts(s);
        return issues.discrepancy > 0
          || issues.missing > 0
          || issues.misalignment > 0
          || (s.kounta?.discrepancyCount || 0) > 0;
      })
      .map((s) => String(s.skuKey));
    if (keys.length === 0 && skus.length > 0) return [String(skus[0].skuKey)];
    return keys;
  }, [skus]);

  useEffect(() => {
    if (!pendingPush) {
      setPushOkReady(false);
      return undefined;
    }
    const id = setTimeout(() => setPushOkReady(true), 400);
    return () => clearTimeout(id);
  }, [pendingPush]);

  const confirmBulk = useCallback(() => {
    const items = skus.map((sku) => ({
      sku,
      resolutions: bulkItems.find((it) => it.skuKey === sku.skuKey)?.resolutions || [],
    }));
    setPendingPush({
      title: 'Apply Guide prices to this beer?',
      changes: buildPushPreview(items, dtcPrices),
      run: () => bulkMutation.mutateAsync(bulkItems),
    });
  }, [skus, bulkItems, bulkMutation, dtcPrices]);

  const requestResolve = useCallback((sku, payload) => {
    const resolutions = payload.resolutions || [];
    const items = skus.some((s) => s.skuKey === sku.skuKey)
      ? skus.map((s) => ({
        sku: s,
        resolutions: s.skuKey === sku.skuKey ? resolutions : [],
      }))
      : [{ sku, resolutions }];
    const changes = buildPushPreview(items, dtcPrices);
    if (payload.kountaCosts) {
      changes.push(...kountaCostPreview(sku, payload.kountaCosts));
    }
    const costOnly = resolutions.length === 0 && payload.kountaCosts;
    setPendingPush({
      title: costOnly ? 'Apply Kounta cost?' : 'Push these prices?',
      changes,
      run: () => resolveMutation.mutateAsync(payload),
    });
  }, [skus, resolveMutation, dtcPrices]);

  const closeDrawer = useCallback(() => {
    if (busy) return;
    setPendingPush(null);
    if (linksDirtyRef.current) {
      linksDirtyRef.current = false;
      queryClient.invalidateQueries({ queryKey: PRICING_DISCREPANCY_SCAN_KEY });
    }
    onClose();
  }, [busy, onClose, queryClient]);

  const meta = titleBeer
    ? [
      titleBeer.productAbv != null && titleBeer.productAbv !== ''
        ? `${titleBeer.productAbv}% ABV`
        : null,
      titleBeer.productType || null,
      titleBeer.productRange || null,
    ].filter(Boolean).join(' · ')
    : '';

  return (
    <Drawer
      title={titleBeer ? (
        <Space size="small" wrap>
          <span>{beerDisplayName(titleBeer)}</span>
          {meta ? <Text type="secondary" style={{ fontWeight: 400 }}>{meta}</Text> : null}
          <Text type="secondary" style={{ fontWeight: 400 }}>
            {(titleBeer.skus || []).length}
            {' '}
            SKU(s)
          </Text>
          <span onClick={stopRowClick} role="presentation">
            <Switch size="small" checked={compact} onChange={setCompact} />
            <Text type="secondary" style={{ marginLeft: 6, fontWeight: 400 }}>
              Only issue rows
            </Text>
          </span>
        </Space>
      ) : 'Details'}
      placement="right"
      width="min(1440px, 96vw)"
      onClose={closeDrawer}
      visible={open}
      footer={beer ? (
        <Space wrap>
          <Button
            type="primary"
            loading={bulkMutation.isLoading}
            disabled={disabled || busy || bulkItems.length === 0}
            onClick={confirmBulk}
          >
            Apply Guide to all mismatches in this beer
            {bulkFieldCount > 0 ? ` (${bulkFieldCount})` : ''}
          </Button>
          <Button onClick={closeDrawer}>Close</Button>
        </Space>
      ) : null}
    >
      {detail.isLoading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : null}
      {detail.error ? (
        <Text type="danger">{errorMessage(detail.error, 'Failed to load beer')}</Text>
      ) : null}
      {beer ? (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Pick one number per price. Rows start untouched. Clear a row to drop that selection.
            Apply writes that number to Matrix and every linked platform: Monday, Cin7, Kounta,
            and Shopify NZ. Link a missing platform below and the comparison refreshes.
            Australia is display-only.
          </Paragraph>

          <Collapse key={`${beer.productMatrixId}-${resetVersion}`} defaultActiveKey={defaultActiveKeys}>
            {skus.map((sku) => (
              <Panel
                key={String(sku.skuKey)}
                header={(
                  <SkuPanelHeader
                    sku={sku}
                    legacyBusy={legacyMutation.isLoading && legacySkuKey === sku.skuKey}
                    onOpenLedger={onOpenLedger}
                    onToggleLegacy={(row, checked) => {
                      if (row.productMatrixId == null || row.productMatrixId === '') {
                        notification.error({ message: 'This SKU is not on a product, so Legacy cannot be saved.' });
                        return;
                      }
                      setLegacySkuKey(row.skuKey);
                      legacyMutation.mutate({ sku: row, isLegacy: checked });
                    }}
                  />
                )}
              >
                <SkuPanelBody
                  key={`${sku.skuKey}-${resetVersion}`}
                  sku={sku}
                  beerName={beerDisplayName(beer)}
                  disabled={disabled}
                  busy={busy}
                  compact={compact}
                  sharedCin7={Array.isArray(cin7Catalog.data) ? cin7Catalog.data : undefined}
                  sharedKounta={sharedKountaCatalog}
                  highlightMissingLinks={highlightMissingLinks}
                  onLinksChanged={refreshBeerDetail}
                  onKountaCatalogSkuChange={patchKountaCatalog}
                  onResolve={(payload) => requestResolve(sku, payload)}
                />
              </Panel>
            ))}
          </Collapse>
        </Space>
      ) : null}
      <Modal
        title={pendingPush?.title || 'Push these prices?'}
        visible={Boolean(pendingPush)}
        zIndex={1300}
        width={960}
        okText="Apply"
        cancelText="Cancel"
        okButtonProps={{ disabled: !pushOkReady }}
        confirmLoading={busy}
        maskClosable={false}
        onCancel={() => {
          if (busy) return;
          setPendingPush(null);
        }}
        onOk={() => pendingPush?.run()}
      >
        <PushPreview changes={pendingPush?.changes || []} />
      </Modal>
    </Drawer>
  );
}

BeerDrawer.propTypes = {
  beerKey: PropTypes.string,
  beerSummary: PropTypes.shape({
    productMatrixId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    productName: PropTypes.string,
    productAbv: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    productType: PropTypes.string,
    productRange: PropTypes.string,
    shopify: PropTypes.shape({}),
    skus: PropTypes.arrayOf(PropTypes.shape({ skuKey: PropTypes.number })),
    counts: PropTypes.shape({}),
  }),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  includeLegacy: PropTypes.bool,
  highlightMissingLinks: PropTypes.bool,
  onOpenLedger: PropTypes.func,
};

BeerDrawer.defaultProps = {
  beerKey: null,
  beerSummary: null,
  disabled: false,
  includeLegacy: false,
  highlightMissingLinks: false,
  onOpenLedger: () => {},
};
